// Microbe RTS — the deterministic lockstep scheduler.
//
// One Lockstep drives ONE local sim in step with the peer's identical sim by exchanging INPUT frames
// (never state). The contract (see net/protocol.ts):
//   - Fixed input delay D: a command submitted while rendering tick T executes at execTick T + D.
//   - EVERY tick each client emits its local input frame (empty on idle ticks) so the peer knows the
//     tick is complete. Frames are keyed by execTick.
//   - A tick N runs only once BOTH the local and remote frames for N are held. Commands then apply in
//     canonical order — side 'you' first, then side 'bot' — followed by sim.update(1/30).
//   - Every CHECKSUM_EVERY ticks each side sends a state hash; a mismatch ends both with 'desync'.
//
// This class is engine/network-agnostic: it talks only to the Transport seam, so the same code runs
// over a real WebSocket or the in-process fake relay in the headless proof.

import type { Command, Side, Sim } from '../types.js';
import type { Transport } from './transport.js';
import type { EndReason, InputMsg, SumMsg } from './protocol.js';
import { CHECKSUM_EVERY, D, SIM_DT } from './protocol.js';
import { hashState } from './hash.js';

export class Lockstep {
  readonly side: Side;
  private sim: Sim;
  private transport: Transport;

  private tick = 0; // next tick to execute (== number of ticks already executed)
  private seq = 0; // per-client input-frame counter

  private pending = new Map<number, Command[]>(); // execTick → staged local cmds awaiting emit
  private localFrames = new Map<number, Command[]>(); // execTick → emitted local frame cmds
  private remoteFrames = new Map<number, Command[]>(); // execTick → received peer frame cmds
  private mySums = new Map<number, string>(); // tick → our hash
  private peerSums = new Map<number, string>(); // tick → peer hash

  private ended = false;
  private endCbs: ((reason: EndReason) => void)[] = [];

  constructor(sim: Sim, side: Side, transport: Transport) {
    this.sim = sim;
    this.side = side;
    this.transport = transport;
  }

  /** Ticks executed so far (== the next tick to run). */
  get currentTick(): number {
    return this.tick;
  }

  onEnd(cb: (reason: EndReason) => void): void {
    this.endCbs.push(cb);
  }

  /** Terminate the session once (idempotent), notifying all onEnd listeners. */
  end(reason: EndReason): void {
    if (this.ended) return;
    this.ended = true;
    for (const cb of this.endCbs) cb(reason);
  }

  /** Queue a local command for execTick = currentTick + D and stage it into that tick's frame. */
  submitCommand(cmd: Command): void {
    if (this.ended) return;
    const execTick = this.tick + D;
    const arr = this.pending.get(execTick);
    if (arr) arr.push(cmd);
    else this.pending.set(execTick, [cmd]);
  }

  /** Ingest a peer input frame (buffered until we reach its tick). First frame per tick wins. */
  receiveInput(msg: InputMsg): void {
    if (this.ended) return;
    if (!this.remoteFrames.has(msg.tick)) this.remoteFrames.set(msg.tick, msg.cmds);
  }

  /** Ingest a peer checksum; compare as soon as we hold our own hash for that tick. */
  receiveSum(msg: SumMsg): void {
    if (this.ended) return;
    if (!this.peerSums.has(msg.tick)) this.peerSums.set(msg.tick, msg.hash);
    this.compareSum(msg.tick);
  }

  private compareSum(t: number): void {
    const mine = this.mySums.get(t);
    const peer = this.peerSums.get(t);
    if (mine != null && peer != null && !this.ended && mine !== peer) {
      // Notify the peer too (best effort) so both sides end even if only one detected it.
      this.transport.send({ t: 'desync', tick: t });
      this.end('desync');
    }
  }

  /** Emit the local input frame for execTick t exactly once. */
  private emitLocal(t: number): void {
    if (this.localFrames.has(t)) return;
    const cmds = this.pending.get(t) ?? [];
    this.pending.delete(t);
    this.localFrames.set(t, cmds);
    this.transport.send({ t: 'input', tick: t, seq: this.seq++, cmds });
  }

  /**
   * Advance exactly one lockstep tick if possible. Returns true if the sim ticked, false if it is
   * still waiting on the peer's frame (or the session has ended). A 30 Hz driver calls this each frame.
   */
  step(): boolean {
    if (this.ended) return false;
    const N = this.tick;

    // Always emit our frame for N first, so the peer can complete its own tick N.
    this.emitLocal(N);

    // Gate: we need the peer's frame for N to proceed.
    const remoteCmds = this.remoteFrames.get(N);
    if (remoteCmds === undefined) return false;
    const localCmds = this.localFrames.get(N) ?? [];

    // Canonical order: all of side 'you' cmds, then all of side 'bot' cmds. Each endpoint knows which
    // frame is which by origin (local frame = our side, remote frame = the peer's side).
    const youCmds = this.side === 'you' ? localCmds : remoteCmds;
    const botCmds = this.side === 'you' ? remoteCmds : localCmds;
    // Route each frame's commands to their ORIGIN side so they drive the right colony (per-side
    // selection/groups). Canonical order preserved: all 'you' commands, then all 'bot' commands.
    for (const c of youCmds) this.sim.command(c, 'you');
    for (const c of botCmds) this.sim.command(c, 'bot');
    this.sim.update(SIM_DT);

    // Reclaim this tick's buffers.
    this.localFrames.delete(N);
    this.remoteFrames.delete(N);
    this.tick = N + 1;

    // Checksum canary at each CHECKSUM_EVERY boundary (keyed by ticks executed).
    if (this.tick % CHECKSUM_EVERY === 0) {
      const h = hashState(this.sim.state);
      this.mySums.set(this.tick, h);
      this.transport.send({ t: 'sum', tick: this.tick, hash: h });
      this.compareSum(this.tick);
    }
    return true;
  }
}
